import { z } from 'zod';

/** Per-account ceiling on cumulative encrypted object storage (R2 + D1 metadata). */
export const ACCOUNT_STORAGE_LIMIT_BYTES = 5 * 1024 ** 3;

export const opaqueId = z.string().min(12).max(256);
export const isoDate = z.string().datetime();

export const encryptedObjectSchema = z.object({
  objectId: opaqueId,
  collectionId: opaqueId,
  kind: z.enum(['blob', 'tree', 'commit', 'manifest']),
  ciphertext: z.string().min(1),
  nonce: z.string().min(1),
  signerPublicKey: z.string().min(1),
  signature: z.string().min(1),
  version: z.literal(1),
});

export const batchObjectsSchema = z.object({
  objects: z.array(encryptedObjectSchema).min(1).max(100),
});

export const objectIdsSchema = z.object({
  ids: z.array(opaqueId).min(1).max(100),
});

/** Vault trust mode: locked = E2E ciphertext; open = server-readable collab Git. */
export const vaultModeSchema = z.enum(['locked', 'open']);
export const vaultRoleSchema = z.enum(['read', 'write', 'admin']);
export const vaultIconSchema = z.string().trim().min(1).max(16);

/** Vault slugs and user handles share this shape. `personal` is a valid slug, not a handle. */
export const nameSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/);

export const RESERVED_HANDLES = [
  'admin',
  'api',
  'auth',
  'clone',
  'connect',
  'device',
  'help',
  'history',
  'login',
  'personal',
  'recovery',
  'settings',
  'shared',
  'support',
  'trove',
  'v1',
  'vault',
  'www',
] as const;

export const reservedHandleSet = new Set<string>(RESERVED_HANDLES);

export const handleSchema = nameSlugSchema.refine((value) => !reservedHandleSet.has(value), {
  message: 'That handle is reserved',
});

export const createRepositorySchema = z.object({
  repositoryId: opaqueId,
  rootCollectionId: opaqueId,
  deviceId: opaqueId,
  deviceEncryptionPublicKey: z.string().min(1),
  deviceSigningPublicKey: z.string().min(1),
  /** Required for locked vaults; empty string for open vaults (no root key). */
  wrappedRootKey: z.string(),
  head: opaqueId,
  mode: vaultModeSchema.default('locked'),
  label: z.string().trim().min(1).max(80).default('Personal'),
  icon: vaultIconSchema.default('lock'),
  slug: nameSlugSchema.optional(),
});

export const updateHeadSchema = z.object({
  expectedVersion: z.number().int().nonnegative(),
  nextHead: opaqueId,
  signature: z.string().min(1),
});

export const shareRightsSchema = z.enum(['read']);
export const createShareSchema = z.object({
  collectionId: opaqueId,
  recipientEmail: z.string().email(),
  rights: z.array(shareRightsSchema).length(1),
  expiresAt: isoDate.nullable(),
  maxViews: z.literal(1).nullable().optional(),
  accessCode: z
    .string()
    .trim()
    .regex(/^[A-Z0-9]{4}-[A-Z0-9]{4}$/)
    .nullable()
    .optional(),
  wrappedKey: z.string().min(1),
  ownerSignature: z.string().min(1),
  collectionLabel: z.string().min(1).max(200),
});

export const createVaultInviteSchema = z.object({
  repositoryId: opaqueId,
  recipientEmail: z.string().email(),
  role: vaultRoleSchema,
});

export const acceptVaultInviteSchema = z.object({
  inviteId: opaqueId,
});

export const gitObjectSchema = z.object({
  objectId: z.string().min(40).max(64),
  kind: z.enum(['blob', 'tree', 'commit', 'tag']),
  /** Base64-encoded Git object content (uncompressed). */
  content: z.string().min(1),
});

export const batchGitObjectsSchema = z.object({
  repositoryId: opaqueId,
  objects: z.array(gitObjectSchema).min(1).max(200),
});

export const updateGitRefSchema = z.object({
  repositoryId: opaqueId,
  name: z.string().min(1).max(255).default('refs/heads/main'),
  objectId: z.string().min(40).max(64),
  expectedObjectId: z.string().min(40).max(64).nullable().optional(),
});

export const gitSearchSchema = z.object({
  repositoryId: opaqueId,
  query: z.string().trim().min(1).max(200),
  limit: z.number().int().min(1).max(100).optional(),
});

export const acceptShareSchema = z.object({
  recipientDeviceId: opaqueId,
  accessCode: z
    .string()
    .trim()
    .regex(/^[A-Z0-9]{4}-[A-Z0-9]{4}$/)
    .optional(),
});

export const magicLinkRequestSchema = z.object({
  email: z
    .string()
    .email()
    .transform((email) => email.trim().toLowerCase()),
  returnTo: z
    .string()
    .regex(/^\/(?!\/)/)
    .max(500)
    .optional(),
});

export const magicLinkVerifySchema = z.object({
  token: z.string().min(32).max(256),
});

export const demoSessionSchema = z.object({ reset: z.boolean().optional() });

export const deviceAuthorizationRequestSchema = z.object({
  label: z.string().trim().min(1).max(80),
  encryptionPublicKey: z.string().min(1).max(1024),
  signingPublicKey: z.string().min(1).max(1024),
  pollTokenHash: z.string().regex(/^[a-f0-9]{64}$/),
});

export const deviceAuthorizationPollSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9]{4}-[A-Z0-9]{4}$/),
  pollToken: z.string().regex(/^[a-f0-9]{64}$/),
});

export const wrappedKeySchema = z.object({
  ephemeralPublicKey: z.string().min(1).max(1024),
  nonce: z.string().min(1).max(256),
  ciphertext: z.string().min(1).max(8192),
});

export const recoveryWrappedKeySchema = z.object({
  nonce: z.string().min(1).max(256),
  ciphertext: z.string().min(1).max(8192),
});

/** Server-stored vault key wraps. Prefer `devices[deviceId]`; `device` is legacy. */
export const rootKeyEnvelopeSchema = z.object({
  device: wrappedKeySchema.optional(),
  recovery: recoveryWrappedKeySchema.optional(),
  devices: z.record(z.string(), wrappedKeySchema).optional(),
});

export const repositorySchema = z.object({
  id: opaqueId,
  rootCollectionId: opaqueId,
  wrappedRootKey: z.string(),
  createdAt: isoDate,
  head: opaqueId,
  version: z.number().int().nonnegative(),
  mode: vaultModeSchema,
  label: z.string().min(1).max(80),
  icon: vaultIconSchema,
  slug: z.string().nullable().optional(),
  ownerHandle: z.string().nullable().optional(),
  role: vaultRoleSchema.optional(),
});

export const repositoriesResponseSchema = z.object({
  repositories: z.array(repositorySchema),
});

export const createRepositoryResponseSchema = z.object({
  ok: z.literal(true),
  repositoryId: opaqueId,
});

export type WrappedKeyPayload = z.infer<typeof wrappedKeySchema>;
export type RecoveryWrappedKeyPayload = z.infer<typeof recoveryWrappedKeySchema>;
export type RootKeyEnvelope = z.infer<typeof rootKeyEnvelopeSchema>;
export type Repository = z.infer<typeof repositorySchema>;
export type RepositoriesResponse = z.infer<typeof repositoriesResponseSchema>;
export type CreateRepositoryResponse = z.infer<typeof createRepositoryResponseSchema>;

export const deviceAuthorizationApprovalSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9]{4}-[A-Z0-9]{4}$/),
  /** Optional wrap of the vault root key to the pending CLI device public key. */
  deviceWrap: wrappedKeySchema.optional(),
  /** Target vault when the account has multiple repositories. */
  repositoryId: opaqueId.optional(),
});

export const registerDeviceSchema = z.object({
  deviceId: opaqueId,
  label: z.string().trim().min(1).max(80),
  encryptionPublicKey: z.string().min(1).max(1024),
  signingPublicKey: z.string().min(1).max(1024),
  /** Optional wrap of the vault root key to this device's encryption public key. */
  deviceWrap: wrappedKeySchema.optional(),
  repositoryId: opaqueId.optional(),
});

const webauthnResponseSchema = z
  .object({
    id: z.string().min(1),
    rawId: z.string().min(1),
    type: z.literal('public-key'),
    response: z.record(z.unknown()),
    clientExtensionResults: z.record(z.unknown()).optional(),
    authenticatorAttachment: z.string().optional(),
  })
  .passthrough();

export const webauthnRegistrationVerifySchema = z.object({
  response: webauthnResponseSchema,
  label: z.string().trim().min(1).max(80).optional(),
  icon: z.enum(['fingerprint', 'laptop', 'smartphone', 'key', 'shield', 'watch']).optional(),
  vaultWraps: z
    .array(
      z.object({
        repositoryId: opaqueId,
        wrappedKey: recoveryWrappedKeySchema,
      }),
    )
    .max(20)
    .optional(),
});

export const webauthnAuthenticationVerifySchema = z.object({
  response: webauthnResponseSchema,
});

export const passkeyUnlockVerifySchema = z.object({
  response: webauthnResponseSchema,
  repositoryId: opaqueId,
  wrappedKey: recoveryWrappedKeySchema,
});

export const updatePasskeySchema = z.object({
  label: z.string().trim().min(1).max(80),
  icon: z.enum(['fingerprint', 'laptop', 'smartphone', 'key', 'shield', 'watch']),
});

export const profileIconIdSchema = z.enum([
  'user',
  'leaf',
  'mountain',
  'sun',
  'moon',
  'sparkles',
  'coffee',
  'book',
]);

export const profileIconBackgroundIdSchema = z.enum([
  'slate',
  'jade',
  'sky',
  'violet',
  'amber',
  'rose',
  'forest',
  'orange',
]);

export const updateProfileSchema = z.object({
  firstName: z.string().trim().min(1).max(40),
  lastName: z.string().trim().min(1).max(40),
  handle: handleSchema.optional(),
  icon: profileIconIdSchema,
  iconBackground: profileIconBackgroundIdSchema.optional(),
  completeOnboarding: z.boolean().optional(),
});

/** Entry glyph overrides + tags live in the encrypted repo root `.trv` file. */
export const entryIconIdSchema = z.enum([
  'folder',
  'file',
  'heart',
  'shield',
  'key',
  'wallet',
  'card',
  'id',
  'lock',
  'star',
  'bookmark',
  'pill',
  'stethoscope',
  'home',
  'briefcase',
  'globe',
]);

export const entryIconColorIdSchema = z.enum([
  'slate',
  'jade',
  'sky',
  'violet',
  'amber',
  'rose',
  'forest',
  'orange',
]);

export const entryAnnotationSchema = z.object({
  icon: entryIconIdSchema.optional(),
  color: entryIconColorIdSchema.optional(),
  tags: z.array(z.string().min(1).max(32)).max(12).optional(),
});

/** Device-local preference: commit each change vs accumulate and flush. */
export const saveModeSchema = z.enum(['immediate', 'queued']);

export type EncryptedObject = z.infer<typeof encryptedObjectSchema>;
export type CreateRepository = z.infer<typeof createRepositorySchema>;
export type CreateShare = z.infer<typeof createShareSchema>;
export type CreateVaultInvite = z.infer<typeof createVaultInviteSchema>;
export type VaultMode = z.infer<typeof vaultModeSchema>;
export type VaultRole = z.infer<typeof vaultRoleSchema>;
export type EntryIconId = z.infer<typeof entryIconIdSchema>;
export type EntryIconColorId = z.infer<typeof entryIconColorIdSchema>;
export type EntryAnnotation = z.infer<typeof entryAnnotationSchema>;
export type SaveMode = z.infer<typeof saveModeSchema>;
export type ProfileIconId = z.infer<typeof profileIconIdSchema>;
export type ProfileIconBackgroundId = z.infer<typeof profileIconBackgroundIdSchema>;
export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  handle: string;
  firstName: string | null;
  lastName: string | null;
  icon: ProfileIconId;
  iconBackground: ProfileIconBackgroundId;
  onboardingComplete: boolean;
  demo: boolean;
}

export interface PasskeyCredential {
  id: string;
  label: string;
  icon: 'fingerprint' | 'laptop' | 'smartphone' | 'key' | 'shield' | 'watch';
  createdAt: string;
  lastUsedAt: string;
  unlockEnabled: boolean;
}

export interface CollectionHead {
  collectionId: string;
  opaqueHeadId: string;
  version: number;
  updatedAt: string;
}

export interface ShareRecord extends Omit<CreateShare, 'accessCode' | 'maxViews'> {
  id: string;
  ownerId: string;
  ownerEmail?: string;
  recipientId: string | null;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  createdAt: string;
  maxViews: 1 | null;
  requiresAccessCode: boolean;
}

export interface AuditEvent {
  id: string;
  action: string;
  targetType: string;
  targetId: string;
  createdAt: string;
  deviceLabel?: string;
}

export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}

export interface StorageUsage {
  usedBytes: number;
  limitBytes: number;
}
